/* Ported from inj-allpages-design/components/primitives.jsx + artboards/*.jsx.
 *
 * One module exporting every shared atom + molecule the design uses
 * (icons, buttons, trust badges, headshots, chips, pills, section
 * headers, phone CTA, lawyer cards, US map stand-in, utility bar,
 * header, footer, mobile CTA bar). Inertia pages import from here.
 *
 * Styling is inline CSS-vars (same as design source) so the visual
 * fidelity is 1:1; refactor to Tailwind later if needed. The CSS
 * variables themselves come from resources/css/tokens.css.
 */

import { Link, usePage } from '@inertiajs/react';
import { type CSSProperties, type ReactNode, useMemo } from 'react';

import { JsonLdScript } from '@/Components/JsonLdScript';
import { MetaHead } from '@/Components/MetaHead';

/* ---------- Icons (Lucide-ish, 1.5px, 20px default) ---------- */

type IconProps = {
    size?: number;
    stroke?: string;
    fill?: string;
    style?: CSSProperties;
    children: ReactNode;
};

const SVG = ({ size = 20, stroke = 'currentColor', fill = 'none', style, children }: IconProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'block', ...style }}
    >
        {children}
    </svg>
);

export const I = {
    search: (
        <SVG>
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
        </SVG>
    ),
    phone: (
        <SVG>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </SVG>
    ),
    pin: (
        <SVG>
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
            <circle cx="12" cy="10" r="3" />
        </SVG>
    ),
    chevron: (
        <SVG>
            <path d="m6 9 6 6 6-6" />
        </SVG>
    ),
    chevronR: (
        <SVG>
            <path d="m9 6 6 6-6 6" />
        </SVG>
    ),
    check: (
        <SVG>
            <path d="M20 6 9 17l-5-5" />
        </SVG>
    ),
    shield: (
        <SVG>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
        </SVG>
    ),
    globe: (
        <SVG>
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 0 20" />
            <path d="M12 2a15.3 15.3 0 0 0 0 20" />
        </SVG>
    ),
    car: (
        <SVG>
            <path d="M5 17H3v-5l3-6h12l3 6v5h-2" />
            <circle cx="7.5" cy="17" r="2.5" />
            <circle cx="16.5" cy="17" r="2.5" />
        </SVG>
    ),
    truck: (
        <SVG>
            <path d="M3 16V5h11v11" />
            <path d="M14 9h4l3 4v3h-7" />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
        </SVG>
    ),
    bike: (
        <SVG>
            <circle cx="6" cy="17" r="3" />
            <circle cx="18" cy="17" r="3" />
            <path d="m9 17 3-7h4l-2 7" />
            <path d="M14 7h3" />
        </SVG>
    ),
    walker: (
        <SVG>
            <circle cx="13" cy="4" r="2" />
            <path d="m8 20 3-6 3 3v3" />
            <path d="m11 14-2-4 4-2 3 3" />
        </SVG>
    ),
    fall: (
        <SVG>
            <path d="M3 21h18" />
            <circle cx="9" cy="5" r="2" />
            <path d="m7 21 2-8 4 2-1 6" />
            <path d="m13 11 4-3" />
        </SVG>
    ),
    med: (
        <SVG>
            <path d="M8 2v4" />
            <path d="M16 2v4" />
            <rect x="4" y="6" width="16" height="14" rx="2" />
            <path d="M12 11v6" />
            <path d="M9 14h6" />
        </SVG>
    ),
    work: (
        <SVG>
            <path d="M2 7h20v13H2z" />
            <path d="M8 7V4h8v3" />
            <path d="M2 13h20" />
        </SVG>
    ),
    dog: (
        <SVG>
            <path d="M8 5 5 8v4l3 3v4h2v-3h4v3h2v-4l3-3V8l-3-3" />
            <circle cx="10" cy="11" r="1" />
            <circle cx="14" cy="11" r="1" />
        </SVG>
    ),
    product: (
        <SVG>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
        </SVG>
    ),
    death: (
        <SVG>
            <path d="M4 21V8l8-5 8 5v13" />
            <path d="M9 21v-6h6v6" />
        </SVG>
    ),
    scales: (
        <SVG>
            <path d="M12 3v18" />
            <path d="M5 21h14" />
            <path d="m6 8 3 6H3l3-6z" />
            <path d="m18 8 3 6h-6l3-6z" />
            <path d="M3 6h18" />
        </SVG>
    ),
    arrow: (
        <SVG>
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
        </SVG>
    ),
    dollar: (
        <SVG>
            <path d="M12 2v20" />
            <path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </SVG>
    ),
    clock: (
        <SVG>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
        </SVG>
    ),
} as const;

export type IconKey = keyof typeof I;

/* ---------- Wordmark ---------- */

export function Wordmark({ size = 22, color, sub }: { size?: number; color?: string; sub?: string }) {
    const c = color ?? 'var(--ink)';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: c }}>
            <svg width={size + 4} height={size + 4} viewBox="0 0 28 28" fill="none">
                <rect x="1.5" y="1.5" width="25" height="25" rx="6" stroke={c} strokeWidth="1.5" />
                <path d="M9 18V8M14 18V8M19 18V13a3 3 0 0 0-3-3" stroke={c} strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="14" cy="21" r="1" fill="var(--ember)" />
            </svg>
            <div style={{ lineHeight: 1 }}>
                <div
                    style={{
                        fontFamily: 'var(--font-serif)',
                        fontWeight: 500,
                        fontSize: size,
                        letterSpacing: '-0.02em',
                    }}
                >
                    injury<span style={{ color: 'var(--ember)' }}>.</span>help
                </div>
                {sub && (
                    <div
                        style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: 10,
                            color: 'var(--ink-mute)',
                            marginTop: 3,
                            letterSpacing: '.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {sub}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- Buttons ---------- */

type BtnKind = 'primary' | 'deep' | 'secondary' | 'ghost' | 'text' | 'pill' | 'success';
type BtnSize = 'sm' | 'md' | 'lg' | 'xl';

const BTN_SIZES: Record<BtnSize, { h: number; px: number; fs: number }> = {
    sm: { h: 36, px: 14, fs: 14 },
    md: { h: 44, px: 18, fs: 15 },
    lg: { h: 52, px: 22, fs: 16 },
    xl: { h: 60, px: 28, fs: 17 },
};

const BTN_KINDS: Record<BtnKind, CSSProperties> = {
    primary: { background: 'var(--ember)', color: '#fff' },
    deep: { background: 'var(--ink)', color: 'var(--paper)' },
    secondary: { background: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--rule)' },
    ghost: { background: 'transparent', color: 'var(--ink)' },
    text: { background: 'transparent', color: 'var(--ember)', padding: 0, height: 'auto' },
    pill: { background: 'var(--ink)', color: 'var(--paper)', borderRadius: 999 },
    success: { background: 'var(--sage)', color: '#fff' },
};

export function Btn({
    kind = 'primary',
    size = 'md',
    icon,
    children,
    full,
    style,
    href,
    external,
    onClick,
    type,
    ...rest
}: {
    kind?: BtnKind;
    size?: BtnSize;
    icon?: ReactNode;
    children?: ReactNode;
    full?: boolean;
    style?: CSSProperties;
    href?: string;
    external?: boolean;
    onClick?: () => void;
    type?: 'button' | 'submit' | 'reset';
}) {
    const sz = BTN_SIZES[size];
    const base: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: sz.h,
        padding: `0 ${sz.px}px`,
        fontSize: sz.fs,
        fontWeight: 500,
        borderRadius: 10,
        border: '1px solid transparent',
        cursor: 'pointer',
        transition: 'all var(--dur) var(--ease)',
        letterSpacing: '-0.005em',
        width: full ? '100%' : 'auto',
        whiteSpace: 'nowrap',
        lineHeight: 1,
        textDecoration: 'none',
    };
    const styles: CSSProperties = { ...base, ...BTN_KINDS[kind], ...style };
    const inner = (
        <>
            {icon && <span style={{ display: 'flex' }}>{icon}</span>}
            {children}
        </>
    );
    if (href) {
        const useNativeAnchor =
            external ||
            href.startsWith('http://') ||
            href.startsWith('https://') ||
            href.startsWith('mailto:') ||
            href.startsWith('tel:') ||
            href.startsWith('/auth/') ||
            href === '/login' ||
            href === '/register';
        if (useNativeAnchor) {
            return (
                <a href={href} style={styles} {...rest}>
                    {inner}
                </a>
            );
        }
        return (
            <Link href={href} style={styles} {...rest}>
                {inner}
            </Link>
        );
    }
    return (
        <button type={type ?? 'button'} onClick={onClick} style={styles} {...rest}>
            {inner}
        </button>
    );
}

/* ---------- Trust badge ---------- */

export function TrustBadge({
    icon = I.shield,
    label,
    tone = 'ink',
}: {
    icon?: ReactNode;
    label: string;
    tone?: 'ink' | 'sage' | 'ember';
}) {
    const tones = { ink: 'var(--ink)', sage: 'var(--sage)', ember: 'var(--ember)' }[tone];
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: 'var(--ink-soft)',
                fontWeight: 500,
            }}
        >
            <span style={{ display: 'inline-flex', color: tones }}>{icon}</span>
            {label}
        </div>
    );
}

/* ---------- Stars ---------- */

export function Stars({ value = 4.5, reviews, size = 14 }: { value?: number; reviews?: number; size?: number }) {
    const stars = [1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        const half = !filled && value >= n - 0.5;
        return (
            <span key={n} style={{ display: 'inline-block', position: 'relative', width: size, height: size, color: 'var(--gold)' }}>
                <span style={{ position: 'absolute', inset: 0, color: 'var(--rule)' }}>
                    <SVG size={size}>
                        <path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                    </SVG>
                </span>
                {(filled || half) && (
                    <span style={{ position: 'absolute', inset: 0, overflow: 'hidden', width: half ? '50%' : '100%' }}>
                        <SVG size={size} fill="currentColor" stroke="none">
                            <path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                        </SVG>
                    </span>
                )}
            </span>
        );
    });
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-soft)' }}>
            <div style={{ display: 'inline-flex', gap: 2 }}>{stars}</div>
            <span>
                <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{value.toFixed(1)}</b>
                {reviews != null && <span style={{ color: 'var(--ink-mute)' }}> · {reviews} review{reviews === 1 ? '' : 's'}</span>}
            </span>
        </div>
    );
}

/* ---------- Headshot (duotone monogram) ---------- */

const HUES = [
    ['#F1DDD3', '#7C2E18'],
    ['#E5DECF', '#4A3A22'],
    ['#DCE7E0', '#2F5544'],
    ['#E2D8DD', '#603A47'],
    ['#D9DEE3', '#2A3849'],
    ['#EDD9C6', '#7A4A22'],
] as const;

export function Headshot({
    initials = 'AN',
    size = 96,
    radius = 12,
    hue = 0,
}: {
    initials?: string;
    size?: number;
    radius?: number;
    hue?: number;
}) {
    const [hi, lo] = HUES[hue % HUES.length]!;
    const id = useMemo(() => 'duo' + Math.random().toString(36).slice(2, 8), []);
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: radius,
                overflow: 'hidden',
                background: hi,
                position: 'relative',
                flexShrink: 0,
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06)',
            }}
        >
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
                <defs>
                    <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={hi} />
                        <stop offset="100%" stopColor={lo} stopOpacity="0.45" />
                    </linearGradient>
                </defs>
                <rect width="100" height="100" fill={`url(#${id})`} />
                <circle cx="50" cy="38" r="16" fill={lo} opacity=".22" />
                <path d="M14 100 C 18 72, 36 62, 50 62 C 64 62, 82 72, 86 100 Z" fill={lo} opacity=".22" />
                <text
                    x="50"
                    y="58"
                    textAnchor="middle"
                    style={{
                        fontFamily: 'Newsreader, Georgia, serif',
                        fontSize: 32,
                        fontWeight: 500,
                        fill: lo,
                        letterSpacing: '-0.02em',
                    }}
                >
                    {initials}
                </text>
            </svg>
        </div>
    );
}

/* ---------- Pill ---------- */

type PillTone = 'ink' | 'sage' | 'ember' | 'paper' | 'outline';
const PILL_TONES: Record<PillTone, CSSProperties> = {
    ink: { background: 'var(--ink)', color: 'var(--paper)' },
    sage: { background: 'var(--sage-tint)', color: 'var(--sage)' },
    ember: { background: 'var(--ember-tint)', color: 'var(--ember-deep)' },
    paper: { background: 'var(--paper-deep)', color: 'var(--ink-soft)' },
    outline: { background: 'transparent', color: 'var(--ink-soft)', border: '1px solid var(--rule)' },
};

export function Pill({ children, tone = 'ink', style }: { children: ReactNode; tone?: PillTone; style?: CSSProperties }) {
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '.01em',
                lineHeight: 1.4,
                ...PILL_TONES[tone],
                ...style,
            }}
        >
            {children}
        </span>
    );
}

/* ---------- Chip ---------- */

export function Chip({
    icon,
    label,
    count,
    selected,
    tone = 'default',
    onClick,
}: {
    icon?: ReactNode;
    label: string;
    count?: number;
    selected?: boolean;
    tone?: 'default' | 'soft' | 'ember' | 'sage';
    onClick?: () => void;
}) {
    const tones = {
        default: { bg: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--rule)' },
        soft: { bg: 'var(--paper-deep)', color: 'var(--ink)', border: '1px solid transparent' },
        ember: { bg: 'var(--ember-tint)', color: 'var(--ember-deep)', border: '1px solid transparent' },
        sage: { bg: 'var(--sage-tint)', color: 'var(--sage)', border: '1px solid transparent' },
    };
    const t = selected
        ? { bg: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' }
        : tones[tone];
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                height: 34,
                padding: '0 12px',
                borderRadius: 999,
                background: t.bg,
                color: t.color,
                border: t.border,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                lineHeight: 1,
                transition: 'all var(--dur) var(--ease)',
            }}
        >
            {icon && <span style={{ display: 'flex', opacity: 0.9 }}>{icon}</span>}
            <span>{label}</span>
            {count != null && (
                <span
                    style={{
                        fontSize: 11,
                        color: selected ? 'rgba(247,243,236,.65)' : 'var(--ink-mute)',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {count.toLocaleString()}
                </span>
            )}
        </button>
    );
}

/* ---------- Section header ---------- */

export function SectionHeader({
    eyebrow,
    title,
    sub,
    action,
}: {
    eyebrow?: string;
    title: string;
    sub?: string;
    action?: ReactNode;
}) {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 24,
                marginBottom: 24,
            }}
        >
            <div style={{ maxWidth: 680 }}>
                {eyebrow && (
                    <div
                        style={{
                            fontSize: 12,
                            letterSpacing: '.12em',
                            textTransform: 'uppercase',
                            color: 'var(--ember)',
                            marginBottom: 8,
                            fontWeight: 500,
                        }}
                    >
                        {eyebrow}
                    </div>
                )}
                <h2 style={{ fontSize: 32, lineHeight: 1.15, marginBottom: sub ? 8 : 0 }}>{title}</h2>
                {sub && <p style={{ color: 'var(--ink-soft)', fontSize: 17, lineHeight: 1.5 }}>{sub}</p>}
            </div>
            {action}
        </div>
    );
}

/* ---------- Phone CTA ---------- */

export function PhoneCTA({
    number = '1-833-INJURY-1',
    kind = 'primary',
    size = 'lg',
    full,
}: {
    number?: string;
    kind?: 'primary' | 'deep' | 'secondary';
    size?: 'lg' | 'md';
    full?: boolean;
}) {
    const sz = size === 'lg' ? { h: 56, fs: 18 } : { h: 44, fs: 15 };
    const styles =
        kind === 'primary'
            ? { bg: 'var(--ember)', color: '#fff', border: '1px solid var(--ember)' }
            : kind === 'deep'
              ? { bg: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' }
              : { bg: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--rule)' };
    const tel = number.replace(/[^0-9+]/g, '');
    return (
        <a
            href={`tel:${tel}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                height: sz.h,
                padding: '0 20px',
                borderRadius: 10,
                background: styles.bg,
                color: styles.color,
                border: styles.border,
                width: full ? '100%' : 'auto',
                textDecoration: 'none',
            }}
        >
            <span style={{ display: 'flex' }}>{I.phone}</span>
            <span className="pip-mono" style={{ fontSize: sz.fs, fontWeight: 500, letterSpacing: '-0.01em' }}>
                {number}
            </span>
        </a>
    );
}

/* ---------- Lawyer Card V (vertical, for featured grids) ---------- */

export type LawyerCardData = {
    name: string;
    firm: string;
    city: string;
    state: string;
    rating: number;
    reviews: number;
    years: number;
    areas: string[];
    langs?: string[];
    initials: string;
    hue: number;
    featured?: boolean;
    href?: string;
    headshotUrl?: string | null;
};

export function LawyerCardV({ data }: { data: LawyerCardData }) {
    return (
        <div
            style={{
                background: 'var(--card)',
                border: '1px solid var(--rule)',
                borderRadius: 14,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    height: 170,
                    background: 'linear-gradient(180deg, #EDE3D3 0%, #E2D5C0 100%)',
                }}
            >
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {data.headshotUrl ? (
                        <img src={data.headshotUrl} alt={data.name} style={{ width: 130, height: 130, borderRadius: 12, objectFit: 'cover' }} />
                    ) : (
                        <Headshot initials={data.initials} hue={data.hue} size={130} radius={12} />
                    )}
                </div>
                {data.featured && (
                    <div style={{ position: 'absolute', top: 12, left: 12 }}>
                        <Pill tone="ember">Featured</Pill>
                    </div>
                )}
                <div style={{ position: 'absolute', top: 12, right: 12 }}>
                    <Pill tone="paper" style={{ background: 'rgba(255,255,255,.9)' }}>
                        <span style={{ color: 'var(--sage)', display: 'inline-flex' }}>{I.check}</span>
                        <span style={{ color: 'var(--ink-soft)' }}>Bar verified</span>
                    </Pill>
                </div>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                <div>
                    <h3 style={{ fontSize: 20, lineHeight: 1.2, marginBottom: 2 }}>{data.name}</h3>
                    <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                        {data.firm} · {data.city}, {data.state}
                    </div>
                </div>
                <Stars value={data.rating} reviews={data.reviews} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.areas.slice(0, 3).map((a) => (
                        <Pill key={a} tone="paper">
                            {a}
                        </Pill>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--ink-mute)', marginTop: 2 }}>
                    <span>
                        <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{data.years}</span> yrs licensed
                    </span>
                    <span>·</span>
                    <span>Free consult</span>
                    {data.langs?.includes('ES') && (
                        <>
                            <span>·</span>
                            <span>EN · ES</span>
                        </>
                    )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 'auto', paddingTop: 12 }}>
                    <Btn kind="secondary" icon={I.phone} size="sm">
                        Call
                    </Btn>
                    {data.href ? (
                        <Btn kind="primary" size="sm" href={data.href}>
                            Profile
                        </Btn>
                    ) : (
                        <Btn kind="primary" size="sm">
                            Profile
                        </Btn>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- Utility bar ---------- */

export function UtilityBar({ phone = '1-833-INJURY-1' }: { phone?: string }) {
    return (
        <div
            data-design-utility
            style={{
                background: 'var(--ink)',
                color: 'var(--paper)',
                padding: '6px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                letterSpacing: '-0.005em',
            }}
        >
            <div data-design-utility-message style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.85 }}>
                <span style={{ display: 'inline-flex', color: 'var(--ember)' }}>{I.phone}</span>
                <span>Speak with a lawyer now — free, 24/7</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="pip-mono" style={{ color: 'var(--paper)', fontWeight: 500, textDecoration: 'none' }}>
                    {phone}
                </a>
                <div style={{ width: 1, height: 14, background: 'rgba(247,243,236,.2)' }} />
                <LocaleSwitcher />
            </div>
        </div>
    );
}

function LocaleSwitcher() {
    const { locale } = (usePage().props ?? {}) as { locale?: string };
    const active = locale === 'es' ? 'es' : 'en';
    const linkStyle = (mine: string): CSSProperties => ({
        color: mine === active ? 'var(--ember)' : 'var(--paper)',
        fontWeight: mine === active ? 600 : 400,
        opacity: mine === active ? 1 : 0.7,
        textDecoration: 'none',
    });
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: 0.85 }}>
            <span style={{ display: 'inline-flex' }}>{I.globe}</span>
            <a href="?lang=en" style={linkStyle('en')}>EN</a>
            <span style={{ opacity: 0.5 }}>/</span>
            <a href="?lang=es" style={linkStyle('es')}>ES</a>
        </div>
    );
}

/* ---------- Header ---------- */

export function DesignHeader({ active }: { active?: string }) {
    const items: { label: string; href: string }[] = [
        { label: 'Find a Lawyer', href: '/search' },
        { label: 'By State', href: '/search' },
        { label: 'Practice Areas', href: '/practice-areas' },
        { label: 'Resources', href: '/resources' },
        { label: 'For Attorneys', href: '/for-attorneys' },
    ];
    return (
        <>
            <MetaHead />
            <JsonLdScript />
            <div
                data-design-header
            style={{
                background: 'var(--paper)',
                borderBottom: '1px solid var(--rule)',
                padding: '18px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 48 }}>
                <Link href="/" style={{ textDecoration: 'none' }}>
                    <Wordmark size={20} />
                </Link>
                <nav data-design-header-nav style={{ display: 'flex', gap: 28 }}>
                    {items.map((it) => (
                        <Link
                            key={it.label}
                            href={it.href}
                            style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: active === it.label ? 'var(--ink)' : 'var(--ink-soft)',
                                borderBottom: active === it.label ? '2px solid var(--ember)' : '2px solid transparent',
                                paddingBottom: 4,
                                textDecoration: 'none',
                            }}
                        >
                            {it.label}
                        </Link>
                    ))}
                </nav>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link
                    data-design-header-search
                    href="/search"
                    style={{
                        width: 40,
                        height: 40,
                        border: '1px solid var(--rule)',
                        borderRadius: 8,
                        background: 'var(--card)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--ink-soft)',
                        cursor: 'pointer',
                        textDecoration: 'none',
                    }}
                >
                    {I.search}
                </Link>
                <Btn kind="deep" size="sm" icon={I.phone} href="/get-matched">
                    Get matched
                </Btn>
            </div>
            </div>
        </>
    );
}

/* ---------- Footer ---------- */

export function DesignFooter() {
    const cols: { h: string; l: { label: string; href: string }[] }[] = [
        {
            h: 'By state',
            l: [
                { label: 'California', href: '/california' },
                { label: 'Texas', href: '/texas' },
                { label: 'Florida', href: '/florida' },
                { label: 'New York', href: '/new-york' },
                { label: 'Illinois', href: '/illinois' },
                { label: 'Pennsylvania', href: '/pennsylvania' },
                { label: 'All 50 states →', href: '/search' },
            ],
        },
        {
            h: 'Practice areas',
            l: [
                { label: 'Car accident', href: '/car-accidents' },
                { label: 'Truck accident', href: '/truck-accidents' },
                { label: 'Motorcycle', href: '/motorcycle-accidents' },
                { label: 'Slip & fall', href: '/slip-and-fall' },
                { label: 'Medical malpractice', href: '/medical-malpractice' },
                { label: "Workers' comp", href: '/workers-compensation' },
                { label: 'Wrongful death', href: '/wrongful-death' },
            ],
        },
        {
            h: 'Resources',
            l: [
                { label: 'After a car accident', href: '/resources' },
                { label: 'How no-win-no-fee works', href: '/resources' },
                { label: 'Statute of limitations', href: '/resources' },
                { label: 'Insurance bad faith', href: '/resources' },
                { label: 'Spanish resources', href: '/resources' },
            ],
        },
        {
            h: 'Company',
            l: [
                { label: 'About', href: '/resources' },
                { label: 'How we vet attorneys', href: '/resources' },
                { label: 'For attorneys', href: '/for-attorneys' },
                { label: 'Press', href: '/resources' },
                { label: 'Contact', href: '/get-matched' },
            ],
        },
        {
            h: 'Legal',
            l: [
                { label: 'Terms', href: '/resources' },
                { label: 'Privacy', href: '/resources' },
                { label: 'Cookie preferences', href: '/resources' },
                { label: 'Advertising disclosures', href: '/resources' },
                { label: 'Do not sell my info', href: '/resources' },
            ],
        },
    ];
    return (
        <div data-design-footer style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '56px 56px 32px' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr repeat(5, 1fr)',
                    gap: 40,
                    paddingBottom: 40,
                    borderBottom: '1px solid rgba(247,243,236,.12)',
                }}
            >
                <div>
                    <Wordmark size={22} color="var(--paper)" />
                    <p style={{ marginTop: 14, fontSize: 13, color: 'rgba(247,243,236,.7)', lineHeight: 1.55, maxWidth: 280 }}>
                        A free directory of bar-verified personal injury attorneys across all 50 states. We don't take fees from you — ever.
                    </p>
                    <div
                        style={{
                            marginTop: 18,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 10px',
                            border: '1px solid rgba(247,243,236,.18)',
                            borderRadius: 999,
                            fontSize: 12,
                            color: 'rgba(247,243,236,.85)',
                        }}
                    >
                        <span style={{ display: 'inline-flex', color: 'var(--ember)' }}>{I.globe}</span>
                        English · Español
                    </div>
                </div>
                {cols.map((c) => (
                    <div key={c.h}>
                        <div
                            style={{
                                fontSize: 11,
                                letterSpacing: '.1em',
                                textTransform: 'uppercase',
                                color: 'var(--ember)',
                                marginBottom: 12,
                            }}
                        >
                            {c.h}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                            {c.l.map((l) => (
                                <Link key={l.label} href={l.href} style={{ fontSize: 13, color: 'rgba(247,243,236,.85)', textDecoration: 'none' }}>
                                    {l.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 24, fontSize: 11, color: 'rgba(247,243,236,.55)', gap: 32 }}>
                <div style={{ maxWidth: 780, lineHeight: 1.6 }}>
                    Attorney advertising. injury.help is not a law firm, lawyer referral service, or legal aid organization. The information on this site is not legal advice and use of this site does not create an attorney-client relationship. Prior results do not guarantee a similar outcome.
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>© {new Date().getFullYear()} injury.help</div>
            </div>
        </div>
    );
}
